import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';
import { Rights, verdictLabel } from '@aiinterview/shared';
import { api } from '../api/client';
import { useSession } from '../api/session';
import {
    Button, Card, TextInput, Select, StatusPill, Loading, ErrorNote, confirmAction, toast
} from '../components/ui';
import { DataTable } from '../components/DataTable';
import { Avatar } from '../components/Avatar';

interface InterviewRow {
    id: number;
    candidateName: string;
    externalRef: string | null;
    templateName: string;
    status: string;
    reviewStatus: string;
    answerCount: number;
    questionCount: number;
    integrityScore: number | null;
    integrityVerdict: string | null;
    aiOverallScore: number | null;
    aiScoringSkipped: boolean;
    invitationSentAt: string | null;
    invitationError: string | null;
    candidateHasEmail: boolean;
    tokenExpiresAt: string;
    completedAt: string | null;
}

const STATUSES = [
    ['', 'All'],
    ['AwaitingReview', 'Waiting for review'],
    ['Pending', 'Not started'],
    ['InProgress', 'Being taken'],
    ['Processing', 'Being processed'],
    ['Completed', 'Completed'],
    ['Cancelled', 'Cancelled']
] as const;

const VERDICT_TONE: Record<string, string> = {
    Clean: 'is-completed',
    Minor: 'is-awaiting',
    Suspicious: 'is-awaiting',
    HighRisk: 'is-bad'
};

const Dash = () => <span style={{ color: 'var(--app-nav-section)' }}>—</span>;

/**
 * Submitted, but still Processing well past the seconds it should take — in practice only when the
 * host stopped mid-run. The server refuses to cancel one any sooner; without this the row offers no
 * button at all and the interview is unreachable.
 */
const stuckInProcessing = (row: InterviewRow): boolean =>
    row.status === 'Processing'
    && !!row.completedAt
    && new Date(row.completedAt).getTime() < Date.now() - 10 * 60_000;

export function Interviews() {
    const [params, setParams] = useSearchParams();
    const [search, setSearch] = useState('');
    const [applied, setApplied] = useState('');
    const status = params.get('status') ?? '';
    const { can } = useSession();
    const queryClient = useQueryClient();

    const { data, isLoading, error } = useQuery({
        queryKey: ['interviews', status, applied],
        queryFn: () => api.get<InterviewRow[]>(
            `/api/admin/interviews?${new URLSearchParams({
                ...(status ? { status } : {}),
                ...(applied ? { search: applied } : {})
            })}`)
    });

    const cancel = useMutation({
        // One endpoint, two situations: cancelling an interview nobody has taken, and closing one the
        // processor abandoned. They need different words, so the caller says which it is.
        mutationFn: ({ id }: { id: number; stuck: boolean }) =>
            api.post(`/api/admin/interviews/${id}/cancel`),
        onSuccess: async (_result, { stuck }) => {
            toast(stuck
                ? 'Interview closed. The answers it did capture are kept.'
                : 'Interview cancelled. The link no longer works.');
            await queryClient.invalidateQueries({ queryKey: ['interviews'] });
        },
        onError: (caught: Error) => toast(caught.message, 'error')
    });

    const resend = useMutation({
        mutationFn: (id: number) =>
            api.post<{ interviewUrl: string; invitationSent: boolean; invitationMessage: string | null }>(
                `/api/admin/interviews/${id}/resend`),
        onSuccess: async (result) => {
            await queryClient.invalidateQueries({ queryKey: ['interviews'] });

            // A fresh link is the only copy that will ever exist, so it is shown rather than
            // announced — even when the email went out.
            await confirmAction({
                title: result.invitationSent ? 'A new link has been emailed' : 'A new link is ready',
                text: result.invitationSent
                    ? `${result.interviewUrl}\n\nThe previous link no longer works.`
                    : `${result.invitationMessage}\n\n${result.interviewUrl}`,
                confirmText: 'Done'
            });
        },
        onError: (caught: Error) => toast(caught.message, 'error')
    });

    return (
        <Card
            heading
            title="Interviews"
            lede="Every interview issued, and where each one got to."
            // Only the two things that create something live in the page header. The filter and the
            // search box belong with the table they filter — in the header they pushed the buttons
            // onto a third line and stretched across half the screen.
            action={can(Rights.Create) && (
                <div className="flex flex-wrap gap-2">
                    <Link to="/candidates/import?mode=interviews">
                        <Button type="button" variant="secondary">
                            <i className="bi bi-upload" aria-hidden="true" />
                            Schedule many
                        </Button>
                    </Link>
                    <Link to="/interviews/new">
                        <Button type="button">
                            <i className="bi bi-plus-lg" aria-hidden="true" />
                            New interview
                        </Button>
                    </Link>
                </div>
            )}
        >
            <form
                className="mb-4 flex flex-wrap items-center gap-2"
                onSubmit={(e) => { e.preventDefault(); setApplied(search.trim()); }}
            >
                <Select
                    value={status}
                    onChange={(e) => {
                        const next = e.target.value;
                        setParams(next ? { status: next } : {});
                    }}
                    className="w-44"
                >
                    {STATUSES.map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                    ))}
                </Select>

                <TextInput
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Candidate or reference"
                    className="w-52"
                />

                <Button type="submit" variant="secondary">
                    <i className="bi bi-search" aria-hidden="true" />
                    Search
                </Button>

                {applied && (
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={() => { setSearch(''); setApplied(''); }}
                    >
                        Clear
                    </Button>
                )}
            </form>

            {error && <ErrorNote message={(error as Error).message} />}

            {isLoading ? <Loading /> : (
                <DataTable
                    rows={data ?? []}
                    rowKey={(row) => row.id}
                    empty="No interviews match this."
                    columns={[
                        {
                            header: 'Candidate',
                            sortBy: (row) => row.candidateName,
                            cell: (row) => (
                                <div className="flex items-center gap-2.5">
                                    <Avatar name={row.candidateName} />
                                    <div className="min-w-0">
                                        <Link
                                            to={`/interviews/${row.id}`}
                                            className="block font-medium hover:underline"
                                            style={{ color: 'var(--app-heading)' }}
                                        >
                                            {row.candidateName}
                                        </Link>
                                        {row.externalRef && (
                                            <span
                                                className="app-mono block"
                                                style={{ color: 'var(--text-muted-safe)' }}
                                            >
                                                {row.externalRef}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )
                        },
                        {
                            header: 'Template',
                            sortBy: (row) => row.templateName,
                            cell: (row) => row.templateName
                        },
                        {
                            header: 'Invitation',
                            sortBy: (row) => (row.invitationSentAt ? 1 : 0),
                            // A silently unsent invitation looks exactly like a slow candidate, so the
                            // reason is put where a recruiter will actually see it.
                            cell: (row) => row.invitationSentAt
                                ? (
                                    <span className="text-xs" style={{ color: 'var(--text-muted-safe)' }}>
                                        {new Date(row.invitationSentAt).toLocaleDateString()}
                                    </span>
                                )
                                : row.invitationError
                                    ? (
                                        <span
                                            className="text-xs"
                                            style={{ color: '#b91c1c' }}
                                            title={row.invitationError}
                                        >
                                            not sent
                                        </span>
                                    )
                                    : <Dash />
                        },
                        {
                            header: 'Status',
                            sortBy: (row) => row.status,
                            cell: (row) => (
                                <StatusPill
                                    kind="status"
                                    value={row.status === 'Completed' && row.reviewStatus === 'Pending'
                                        ? 'AwaitingReview'
                                        : row.status}
                                />
                            )
                        },
                        {
                            header: 'Progress',
                            sortBy: (row) => row.answerCount,
                            cell: (row) => row.answerCount > 0
                                ? `${row.answerCount} / ${row.questionCount}`
                                : <Dash />
                        },
                        {
                            header: 'Integrity',
                            sortBy: (row) => row.integrityScore,
                            // Score and verdict together: the number alone means nothing to anyone who
                            // has not memorised the bands.
                            cell: (row) => row.integrityScore === null
                                ? <Dash />
                                : (
                                    <span
                                        className={`status-pill ${VERDICT_TONE[row.integrityVerdict ?? ''] ?? 'is-pending'}`}
                                    >
                                        {row.integrityScore} · {verdictLabel(row.integrityVerdict ?? '')}
                                    </span>
                                )
                        },
                        {
                            header: 'AI score',
                            sortBy: (row) => row.aiOverallScore,
                            cell: (row) => row.aiScoringSkipped
                                ? (
                                    <span className="text-xs" style={{ color: 'var(--text-muted-safe)' }}>
                                        Not scored
                                    </span>
                                )
                                : row.aiOverallScore ?? <Dash />
                        },
                        {
                            header: 'Review',
                            sortBy: (row) => row.reviewStatus,
                            cell: (row) => row.status === 'Completed'
                                ? <StatusPill kind="review" value={row.reviewStatus} />
                                : <Dash />
                        },
                        {
                            header: 'Actions',
                            className: 'text-right',
                            cell: (row) => {
                                const finished = row.status === 'Completed' || row.status === 'Processing';
                                const cancellable = row.status === 'Pending' || row.status === 'InProgress';

                                return (
                                    <div className="flex justify-end gap-2">
                                        {finished && (
                                            <Link to={`/interviews/${row.id}`}>
                                                <Button variant="secondary">
                                                    <i className="bi bi-eye" aria-hidden="true" />
                                                    View report
                                                </Button>
                                            </Link>
                                        )}

                                        {can(Rights.Update) && stuckInProcessing(row) && (
                                            <Button
                                                variant="danger-quiet"
                                                onClick={async () => {
                                                    const confirmed = await confirmAction({
                                                        title: 'Close this interview?',
                                                        text: 'It got stuck part-way through processing. '
                                                            + 'Closing it keeps the answers already captured, '
                                                            + 'and its link stops working.',
                                                        confirmText: 'Close it',
                                                        danger: true
                                                    });
                                                    if (confirmed) cancel.mutate({ id: row.id, stuck: true });
                                                }}
                                            >
                                                <i className="bi bi-x-circle" aria-hidden="true" />
                                                Close
                                            </Button>
                                        )}

                                        {can(Rights.Create) && row.status === 'Pending' && (
                                            <Button
                                                variant="secondary"
                                                busy={resend.isPending && resend.variables === row.id}
                                                title={row.candidateHasEmail
                                                    ? 'Mint a new link and email it. The old one stops working.'
                                                    : 'This candidate has no email address, so the new link will be shown for you to copy.'}
                                                onClick={() => resend.mutate(row.id)}
                                            >
                                                <i className="bi bi-send" aria-hidden="true" />
                                                Resend
                                            </Button>
                                        )}

                                        {can(Rights.Update) && cancellable && (
                                            <Button
                                                variant="danger-quiet"
                                                onClick={async () => {
                                                    const confirmed = await confirmAction({
                                                        title: `Cancel ${row.candidateName}'s interview?`,
                                                        text: 'Their link will stop working.',
                                                        confirmText: 'Cancel interview',
                                                        danger: true
                                                    });
                                                    if (confirmed) cancel.mutate({ id: row.id, stuck: false });
                                                }}
                                            >
                                                <i className="bi bi-x-circle" aria-hidden="true" />
                                                Cancel
                                            </Button>
                                        )}
                                    </div>
                                );
                            }
                        }
                    ]}
                />
            )}
        </Card>
    );
}
